/** Four hundred and four: off track.
 *
 * The car leaves the racing line at the corner exit and fades into the
 * gravel, which is about what happened to the address. The page says what was
 * asked for, in the monospace line, because the commonest cause is a link
 * somebody else wrote — and "which link" is the first thing they will be
 * asked.
 *
 * A signed-out visitor to an admin route gets this page too, and is not told
 * the route exists. A signed-in one who is not an admin gets `Forbidden`
 * instead; `RequireAdmin` in `lib/session.tsx` says why.
 */

import { Link, useLocation } from 'react-router-dom';
import { FlagPage } from '@/components/ui/FlagPage';

/** The circuit, and the line that does not make the corner. One stylised
 *  path, as in the design — this is a picture of a mistake, not of a track. */
const CIRCUIT =
  'M 80 300 L 380 300 C 440 300 470 270 455 225 L 430 160 C 418 125 445 95 405 82 L 290 65 C 250 60 235 90 260 118 L 300 152 C 320 172 300 200 265 194 L 170 176 C 130 170 105 145 80 152 C 40 162 30 210 50 245 C 60 265 55 300 80 300 Z';

function OffTrack() {
  return (
    <svg viewBox="0 0 560 400" style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      <path
        d={CIRCUIT}
        fill="none"
        stroke="var(--color-neutral-800)"
        strokeWidth={24}
        strokeLinejoin="round"
      />
      <path
        d="M 80 300 L 380 300 C 440 300 470 270 455 225 L 430 160"
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth={1.5}
        strokeDasharray="6 6"
        opacity={0.5}
      />
      <path
        d="M 300 300 L 380 300 C 440 300 470 270 455 225 L 430 160 C 424 143 470 120 520 98 C 545 87 555 80 560 78"
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray="80 480"
        style={{
          animation: 'offtrack 3.2s ease-in infinite',
          filter: 'drop-shadow(0 0 6px var(--color-accent))',
        }}
      />
      <g fill="none" stroke="var(--color-neutral-700)" strokeWidth={1}>
        <circle cx={480} cy={120} r={2} />
        <circle cx={498} cy={104} r={1.5} />
        <circle cx={512} cy={126} r={1} />
        <circle cx={530} cy={98} r={2} />
        <circle cx={470} cy={140} r={1} />
      </g>
      <text
        x={470}
        y={165}
        fontSize={11}
        fill="var(--color-neutral-600)"
        fontFamily="var(--font-mono)"
      >
        gravel
      </text>
      <rect x={78} y={290} width={4} height={20} fill="var(--color-neutral-300)" />
    </svg>
  );
}

export function NotFound() {
  const { pathname } = useLocation();

  return (
    <FlagPage
      status={404}
      swatch="flag-swatch-chequer"
      kicker="Off track · sector 2"
      title="You've gone wide. This corner doesn't exist."
      glow="radial-gradient(ellipse 50% 60% at 70% 50%, color-mix(in srgb, var(--color-accent) 8%, transparent), transparent 70%)"
      actions={
        <>
          <Link className="btn btn-primary" to="/">
            Back to pit lane
          </Link>
          <Link className="btn btn-secondary" to="/sessions">
            Browse sessions
          </Link>
        </>
      }
      diag={<>GET {pathname.slice(0, 200)} · no lap on record</>}
      art={<OffTrack />}
    >
      The page you asked for isn't on the circuit map. It may have been renamed, deleted, or never
      recorded in the first place.
    </FlagPage>
  );
}

export default NotFound;
